import type {
	ScoreCriteriaCriterion,
	ScoreCriteriaResponse,
} from "@/pages/Private/pages/Score/schema/score.schema";

/**
 * IMPORTANT: this module must stay free of runtime imports (types only).
 * score.schema.ts sits in an import cycle (via the @/utils barrel), so a
 * value exported from there can still be undefined when score.api injects
 * its endpoints — a silently-skipped transformResponse.
 */

const coerceToArray = (value: unknown): unknown =>
	Array.isArray(value) ? value : value == null ? [] : [value];

/**
 * Endpoint-level normalization for score/criteria responses, used as RTKQ
 * transformResponse by getScoreCriteria/updateScoreCriteria. The AI-written
 * criteria JSON sometimes lands with an OTHER group as a single object
 * instead of an array of groups (seen after the onboarding-test suggestion
 * apply, PREC-3700) — normalize at the boundary so consumers always get an
 * array; the next PATCH writes the healed shape back. (The base query's
 * responseSchema validation is disabled, so Zod-level preprocessing alone
 * never runs at runtime.)
 */
export const normalizeScoreCriteriaResponse = (
	response: ScoreCriteriaResponse
): ScoreCriteriaResponse => ({
	...response,
	allCriteria: (response.allCriteria ?? []).map((entry) => ({
		...entry,
		criteria: {
			...entry.criteria,
			companyScoring: {
				...entry.criteria.companyScoring,
				OTHER: coerceToArray(
					entry.criteria.companyScoring.OTHER
				) as ScoreCriteriaCriterion["companyScoring"]["OTHER"],
			},
			personaScoring: {
				...entry.criteria.personaScoring,
				OTHER: coerceToArray(
					entry.criteria.personaScoring.OTHER
				) as ScoreCriteriaCriterion["personaScoring"]["OTHER"],
			},
		},
	})),
});
